import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import MainLayout, { BreadcrumbItem } from '../../components/MainLayout';
import DepartamentosPageView from './DepartamentosPageView';
import AnadirElementoModal, { IDepartamentoFormData, ISimpleFormData } from './AnadirElementoModal';
import ConfirmarEliminarElementoModal from './ConfirmarEliminarElementoModal';
import { IDepartment, IAcademy, ISubject } from '../../types/departamento';
import { RootState } from '../../state-management/store';
import { setDepartments, setAcademies, setSubjects } from '../../state-management/slices/DepartamentosPage';
import {
	apiErrorMessage,
	createAcademy,
	createDepartment,
	createSubject,
	deleteAcademy,
	deleteDepartment,
	deleteSubject,
	httpImageUrl,
	listAcademies,
	listDepartments,
	listSubjects,
	updateAcademy,
	updateDepartment,
	updateSubject,
} from '../../api/catalog';

type DeletingEntity = {
	tipo: 'departamento' | 'academia' | 'materia';
	item: IDepartment | IAcademy | ISubject;
	cascadeInfo?: { academias?: number; materias?: number };
} | null;

type EditTarget =
	| { tipo: 'departamento'; item: IDepartment }
	| { tipo: 'academia'; item: IAcademy }
	| { tipo: 'materia'; item: ISubject }
	| null;

const DepartamentosPage: React.FC = () => {
	const dispatch = useDispatch();

	const [selectedDepartment, setSelectedDepartment] = useState<IDepartment | null>(null);
	const [selectedAcademy, setSelectedAcademy] = useState<IAcademy | null>(null);
	const [isAddModalOpen, setIsAddModalOpen] = useState(false);
	const [editTarget, setEditTarget] = useState<EditTarget>(null);
	const [deletingEntity, setDeletingEntity] = useState<DeletingEntity>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [loadError, setLoadError] = useState<string | null>(null);
	const seenDepartmentId = useRef<number | undefined>(undefined);
	const loadGeneration = useRef(0);

	const { departments, academies, subjects } = useSelector(
		(state: RootState) => state.departamentosPage
	);

	const applyDepartments = useCallback(
		(rows: IDepartment[]) => {
			dispatch(setDepartments(rows));
			setSelectedDepartment((current) => {
				if (!current) return null;
				return rows.find((row) => row.id === current.id) ?? null;
			});
		},
		[dispatch]
	);

	const applyAcademies = useCallback(
		(rows: IAcademy[]) => {
			dispatch(setAcademies(rows));
			setSelectedAcademy((current) => {
				if (!current) return null;
				return rows.find((row) => row.id === current.id) ?? null;
			});
		},
		[dispatch]
	);

	const applySubjects = useCallback(
		(rows: ISubject[]) => {
			dispatch(setSubjects(rows));
		},
		[dispatch]
	);

	const reloadAfterWrite = async (reload: (generation: number) => Promise<void>) => {
		const generation = ++loadGeneration.current;
		try {
			await reload(generation);
		} catch (error) {
			if (loadGeneration.current === generation) setLoadError(apiErrorMessage(error));
		}
	};

	useEffect(() => {
		const departmentId = selectedDepartment?.id;
		const academyId = selectedAcademy?.id;
		const departmentChanged = seenDepartmentId.current !== departmentId;
		seenDepartmentId.current = departmentId;

		const generation = ++loadGeneration.current;
		let cancelled = false;
		setIsLoading(true);
		setLoadError(null);

		if (departmentChanged) {
			dispatch(setAcademies([]));
			dispatch(setSubjects([]));
		} else if (academyId !== undefined) {
			dispatch(setSubjects([]));
		}

		const stillCurrent = () => !cancelled && loadGeneration.current === generation;

		const load = async () => {
			if (departmentId !== undefined && academyId !== undefined) {
				const rows = await listSubjects(academyId);
				if (!stillCurrent()) return;
				dispatch(setSubjects(rows));
				return;
			}
			if (departmentId !== undefined) {
				const rows = await listAcademies(departmentId);
				if (!stillCurrent()) return;
				dispatch(setAcademies(rows));
				dispatch(setSubjects([]));
				return;
			}
			const rows = await listDepartments();
			if (!stillCurrent()) return;
			dispatch(setDepartments(rows));
		};

		load()
			.catch((error: unknown) => {
				if (stillCurrent()) setLoadError(apiErrorMessage(error));
			})
			.finally(() => {
				if (!cancelled) setIsLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [selectedDepartment?.id, selectedAcademy?.id, dispatch]);

	const academyCountByDeptId = useMemo(() => {
		const counts: Record<number, number> = {};
		for (const department of departments) counts[department.id] = department.academyCount;
		return counts;
	}, [departments]);

	const subjectCountByAcadId = useMemo(() => {
		const counts: Record<number, number> = {};
		for (const academy of academies) counts[academy.id] = academy.subjectCount;
		return counts;
	}, [academies]);

	const handleDepartmentClick = (dept: IDepartment) => {
		setSelectedAcademy(null);
		setSelectedDepartment(dept);
	};

	const handleAcademyClick = (acad: IAcademy) => setSelectedAcademy(acad);

	const handleBackToDepartments = () => {
		setSelectedAcademy(null);
		setSelectedDepartment(null);
	};

	const handleBackToAcademies = () => setSelectedAcademy(null);

	const addModalTipo = (() => {
		if (selectedDepartment && selectedAcademy) return 'materia' as const;
		if (selectedDepartment) return 'academia' as const;
		return 'departamento' as const;
	})();

	const handleConfirmarDepartamento = async (data: IDepartamentoFormData) => {
		await createDepartment(
			{
				name: data.name,
				description: data.description,
				imageUrl: httpImageUrl(data.imageUrl),
				color: data.color,
			},
			data.imageFile
		);
		setIsAddModalOpen(false);
		await reloadAfterWrite(async (generation) => {
			const rows = await listDepartments();
			if (loadGeneration.current !== generation) return;
			applyDepartments(rows);
		});
	};

	const handleConfirmarAcademia = async (data: ISimpleFormData) => {
		if (!selectedDepartment) return;
		const departmentId = selectedDepartment.id;
		await createAcademy(departmentId, { name: data.name });
		setIsAddModalOpen(false);
		await reloadAfterWrite(async (generation) => {
			const academyRows = await listAcademies(departmentId);
			if (loadGeneration.current !== generation) return;
			applyAcademies(academyRows);
			const departmentRows = await listDepartments();
			if (loadGeneration.current !== generation) return;
			applyDepartments(departmentRows);
		});
	};

	const handleConfirmarMateria = async (data: ISimpleFormData) => {
		if (!selectedDepartment || !selectedAcademy) return;
		const departmentId = selectedDepartment.id;
		const academyId = selectedAcademy.id;
		await createSubject(academyId, { name: data.name });
		setIsAddModalOpen(false);
		await reloadAfterWrite(async (generation) => {
			const subjectRows = await listSubjects(academyId);
			if (loadGeneration.current !== generation) return;
			applySubjects(subjectRows);
			const academyRows = await listAcademies(departmentId);
			if (loadGeneration.current !== generation) return;
			applyAcademies(academyRows);
			const departmentRows = await listDepartments();
			if (loadGeneration.current !== generation) return;
			applyDepartments(departmentRows);
		});
	};

	const handleEditDepartment = (dept: IDepartment) => setEditTarget({ tipo: 'departamento', item: dept });

	const handleEditAcademy = (acad: IAcademy) => setEditTarget({ tipo: 'academia', item: acad });

	const handleEditSubject = (subj: ISubject) => setEditTarget({ tipo: 'materia', item: subj });

	const handleConfirmarEdicion = async (data: IDepartamentoFormData | ISimpleFormData) => {
		if (!editTarget) return;
		if (editTarget.tipo === 'departamento') {
			const current = editTarget.item;
			const form = data as IDepartamentoFormData;
			await updateDepartment(
				current.id,
				{
					name: form.name,
					description: form.description,
					imageUrl: httpImageUrl(form.imageUrl),
					color: form.color,
				},
				form.imageFile
			);
			setEditTarget(null);
			await reloadAfterWrite(async (generation) => {
				const rows = await listDepartments();
				if (loadGeneration.current !== generation) return;
				applyDepartments(rows);
			});
			return;
		}
		if (editTarget.tipo === 'academia') {
			const current = editTarget.item;
			const departmentId = selectedDepartment?.id;
			await updateAcademy(current.id, { name: (data as ISimpleFormData).name });
			setEditTarget(null);
			await reloadAfterWrite(async (generation) => {
				if (departmentId !== undefined) {
					const academyRows = await listAcademies(departmentId);
					if (loadGeneration.current !== generation) return;
					applyAcademies(academyRows);
				}
				const departmentRows = await listDepartments();
				if (loadGeneration.current !== generation) return;
				applyDepartments(departmentRows);
			});
			return;
		}
		const current = editTarget.item;
		const academyId = selectedAcademy?.id;
		await updateSubject(current.id, { name: (data as ISimpleFormData).name });
		setEditTarget(null);
		await reloadAfterWrite(async (generation) => {
			if (academyId === undefined) return;
			const rows = await listSubjects(academyId);
			if (loadGeneration.current !== generation) return;
			applySubjects(rows);
		});
	};

	const handleDeleteDepartment = (dept: IDepartment) => {
		setDeletingEntity({
			tipo: 'departamento',
			item: dept,
			cascadeInfo: {
				academias: dept.academyCount,
				materias: dept.subjectCount,
			},
		});
	};

	const handleDeleteAcademy = (acad: IAcademy) => {
		setDeletingEntity({
			tipo: 'academia',
			item: acad,
			cascadeInfo: { materias: acad.subjectCount },
		});
	};

	const handleDeleteSubject = (subj: ISubject) => {
		setDeletingEntity({ tipo: 'materia', item: subj });
	};

	const handleConfirmarEliminar = async () => {
		if (!deletingEntity) return;
		const { tipo, item } = deletingEntity;
		const departmentId = selectedDepartment?.id;
		const academyId = selectedAcademy?.id;

		if (tipo === 'departamento') await deleteDepartment(item.id);
		else if (tipo === 'academia') await deleteAcademy(item.id);
		else await deleteSubject(item.id);

		setDeletingEntity(null);
		await reloadAfterWrite(async (generation) => {
			if (tipo === 'departamento') {
				const rows = await listDepartments();
				if (loadGeneration.current !== generation) return;
				applyDepartments(rows);
				return;
			}
			if (tipo === 'academia' && departmentId !== undefined) {
				const academyRows = await listAcademies(departmentId);
				if (loadGeneration.current !== generation) return;
				applyAcademies(academyRows);
				const departmentRows = await listDepartments();
				if (loadGeneration.current !== generation) return;
				applyDepartments(departmentRows);
				return;
			}
			if (academyId !== undefined) {
				const subjectRows = await listSubjects(academyId);
				if (loadGeneration.current !== generation) return;
				applySubjects(subjectRows);
			}
			if (departmentId !== undefined) {
				const academyRows = await listAcademies(departmentId);
				if (loadGeneration.current !== generation) return;
				applyAcademies(academyRows);
				const departmentRows = await listDepartments();
				if (loadGeneration.current !== generation) return;
				applyDepartments(departmentRows);
			}
		});
	};

	const breadcrumbs: BreadcrumbItem[] = (() => {
		const crumbs: BreadcrumbItem[] = [{ label: 'Departamentos', onClick: handleBackToDepartments }];
		if (selectedDepartment) crumbs.push({ label: selectedDepartment.name, onClick: handleBackToAcademies });
		if (selectedAcademy) crumbs.push({ label: selectedAcademy.name });
		return crumbs;
	})();

	const { pageTitle, pageSubtitle } = (() => {
		if (selectedDepartment && selectedAcademy) {
			return { pageTitle: selectedAcademy.name, pageSubtitle: 'Gestiona las materias de esta academia' };
		}
		if (selectedDepartment) {
			return { pageTitle: selectedDepartment.name, pageSubtitle: 'Gestiona las academias de este departamento' };
		}
		return {
			pageTitle: 'Departamentos Académicos',
			pageSubtitle: 'Gestiona la estructura de departamentos, academias y materias',
		};
	})();

	const editingDepartment = editTarget?.tipo === 'departamento' ? editTarget.item : null;

	return (
		<MainLayout {...{ pageTitle, pageSubtitle, breadcrumbs }}>
			<DepartamentosPageView
				departments={departments}
				academies={academies}
				subjects={subjects}
				selectedDepartment={selectedDepartment}
				selectedAcademy={selectedAcademy}
				onDepartmentClick={handleDepartmentClick}
				onAcademyClick={handleAcademyClick}
				onEditDepartment={handleEditDepartment}
				onDeleteDepartment={handleDeleteDepartment}
				onEditAcademy={handleEditAcademy}
				onDeleteAcademy={handleDeleteAcademy}
				onEditSubject={handleEditSubject}
				onDeleteSubject={handleDeleteSubject}
				academyCountByDeptId={academyCountByDeptId}
				subjectCountByAcadId={subjectCountByAcadId}
				onAddClick={() => setIsAddModalOpen(true)}
				isLoading={isLoading}
				loadError={loadError}
			/>

			{addModalTipo === 'departamento' && (
				<AnadirElementoModal
					tipo='departamento'
					open={isAddModalOpen}
					onClose={() => setIsAddModalOpen(false)}
					onConfirmar={handleConfirmarDepartamento}
				/>
			)}
			{addModalTipo === 'academia' && (
				<AnadirElementoModal
					tipo='academia'
					open={isAddModalOpen}
					onClose={() => setIsAddModalOpen(false)}
					onConfirmar={handleConfirmarAcademia}
				/>
			)}
			{addModalTipo === 'materia' && (
				<AnadirElementoModal
					tipo='materia'
					open={isAddModalOpen}
					onClose={() => setIsAddModalOpen(false)}
					onConfirmar={handleConfirmarMateria}
				/>
			)}

			{editingDepartment && (
				<AnadirElementoModal
					tipo='departamento'
					open
					initialData={{
						name: editingDepartment.name,
						description: editingDepartment.description,
						imageUrl: httpImageUrl(editingDepartment.imageUrl) ?? undefined,
						color: editingDepartment.color,
					}}
					onClose={() => setEditTarget(null)}
					onConfirmar={handleConfirmarEdicion}
				/>
			)}
			{editTarget?.tipo === 'academia' && (
				<AnadirElementoModal
					tipo='academia'
					open
					initialData={{ name: editTarget.item.name }}
					onClose={() => setEditTarget(null)}
					onConfirmar={handleConfirmarEdicion}
				/>
			)}
			{editTarget?.tipo === 'materia' && (
				<AnadirElementoModal
					tipo='materia'
					open
					initialData={{ name: editTarget.item.name }}
					onClose={() => setEditTarget(null)}
					onConfirmar={handleConfirmarEdicion}
				/>
			)}

			<ConfirmarEliminarElementoModal
				open={deletingEntity !== null}
				tipo={deletingEntity?.tipo ?? 'departamento'}
				nombre={deletingEntity?.item.name ?? ''}
				cascadeInfo={deletingEntity?.cascadeInfo}
				onConfirm={handleConfirmarEliminar}
				onCancel={() => setDeletingEntity(null)}
			/>
		</MainLayout>
	);
};

export default DepartamentosPage;
